import React, { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { TrendingUp, Menu, X, ArrowRight } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../utils/cn'
import Footer from './Footer'

const NAV = [
  { label: 'Features', to: '/features' },
  { label: 'About',    to: '/about' },
  { label: 'FAQ',      to: '/faq' },
  { label: 'Contact',  to: '/contact' },
]

/**
 * PublicLayout — the unauthenticated / marketing shell. Slim themed top bar,
 * page content, and the full footer. Used for About, Features, Contact, FAQ,
 * Privacy, Terms, and the auth screens.
 */
export default function PublicLayout() {
  const { user } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-ink-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to={user ? '/dashboard' : '/login'} className="flex items-center gap-2.5 shrink-0">
            <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 shadow-glow-accent flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-semibold text-ink-900 text-lg tracking-tight">serenDPT</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => cn(
                  'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive ? 'text-accent-700 bg-accent-50' : 'text-ink-500 hover:text-ink-900 hover:bg-ink-50',
                )}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          {/* Auth CTA */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Link to="/dashboard" className="btn btn-primary inline-flex items-center gap-1.5 text-sm">
                Open app <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <>
                {location.pathname !== '/login' && (
                  <Link to="/login" className="px-3 py-2 rounded-lg text-sm font-medium text-ink-600 hover:text-ink-900 hover:bg-ink-50 transition-colors">
                    Sign in
                  </Link>
                )}
                <Link to="/register" className="btn btn-primary inline-flex items-center gap-1.5 text-sm">
                  Get started <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button onClick={() => setOpen((o) => !o)} className="md:hidden p-2 rounded-lg text-ink-600 hover:bg-ink-50" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-ink-100 bg-white animate-fade-in">
            <nav className="px-4 py-3 space-y-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => cn(
                    'block px-3 py-2 rounded-lg text-sm font-medium',
                    isActive ? 'text-accent-700 bg-accent-50' : 'text-ink-600 hover:bg-ink-50',
                  )}
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-2 flex gap-2">
                <Link to={user ? '/dashboard' : '/login'} onClick={() => setOpen(false)} className="btn btn-secondary flex-1 text-sm justify-center">
                  {user ? 'Open app' : 'Sign in'}
                </Link>
                {!user && (
                  <Link to="/register" onClick={() => setOpen(false)} className="btn btn-primary flex-1 text-sm justify-center">
                    Get started
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Page content */}
      <main key={location.pathname} className="flex-1 animate-rise">
        <Outlet />
      </main>

      <Footer variant="full" />
    </div>
  )
}
